import { useState, useEffect } from 'react';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Hardcoded data for demonstration
const timeRanges = ['Last 7 Days', 'Last 30 Days', 'Last 90 Days', 'Last Year', 'All Time'];

const mockData = {
  // Campaign performance data
  campaignPerformance: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Click Rate (%)',
        data: [28, 32, 26, 24, 20, 18, 16, 15, 13, 12, 10, 8],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
        tension: 0.3,
      },
      {
        label: 'Report Rate (%)',
        data: [45, 50, 53, 58, 62, 65, 68, 73, 76, 78, 82, 85],
        borderColor: 'rgb(53, 162, 235)',
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
        tension: 0.3,
      },
    ],
  },
  
  // User training progress
  trainingProgress: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Average Completion Rate (%)',
        data: [10, 15, 25, 35, 42, 50, 58, 65, 72, 78, 85, 92],
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgb(75, 192, 192)',
        borderWidth: 1,
        fill: true,
        tension: 0.4,
      },
    ],
  },
  
  // Phishing behavior by department
  departmentPerformance: {
    labels: ['IT', 'Finance', 'HR', 'Marketing', 'Operations', 'Sales', 'Executive'],
    datasets: [
      {
        label: 'Click Rate (%)',
        data: [8, 15, 18, 25, 20, 28, 12],
        backgroundColor: 'rgba(255, 99, 132, 0.5)',
      },
      {
        label: 'Report Rate (%)',
        data: [85, 70, 65, 60, 68, 55, 75],
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  },
  
  // Most targeted phishing types
  phishingTypes: {
    labels: ['Password Reset', 'Account Verification', 'Invoice', 'Delivery Notification', 'IT Support', 'Executive Request', 'Other'],
    datasets: [
      {
        data: [25, 18, 15, 12, 10, 8, 12],
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)',
          'rgba(199, 199, 199, 0.5)',
        ],
        borderWidth: 1,
      },
    ],
  },
  
  // Recent campaign results
  recentCampaigns: [
    {
      id: 1,
      name: 'Q3 Security Awareness',
      startDate: '2023-07-15',
      endDate: '2023-07-22',
      targets: 240,
      delivered: 238,
      opened: 218,
      clicked: 45,
      reported: 173,
      clickRate: '18.9%',
      reportRate: '79.4%',
    },
    {
      id: 2,
      name: 'Password Reset Simulation',
      startDate: '2023-10-10',
      endDate: '2023-10-17',
      targets: 256,
      delivered: 250,
      opened: 220,
      clicked: 62,
      reported: 158,
      clickRate: '28.2%',
      reportRate: '71.8%',
    },
    {
      id: 3,
      name: 'Invoice Phishing Test',
      startDate: '2023-08-22',
      endDate: '2023-08-29',
      targets: 240,
      delivered: 235,
      opened: 200,
      clicked: 28,
      reported: 172,
      clickRate: '14.0%',
      reportRate: '86.0%',
    },
  ],
  
  // User awareness trends
  awarenessScore: {
    current: 78,
    target: 90,
    initial: 62,
    byDepartment: [
      { name: 'IT', score: 92 },
      { name: 'Finance', score: 84 },
      { name: 'HR', score: 80 },
      { name: 'Marketing', score: 70 },
      { name: 'Operations', score: 76 },
      { name: 'Sales', score: 68 },
      { name: 'Executive', score: 86 },
    ]
  },
};

// Add these chart options after the mockData declaration
const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
    }
  },
  plugins: {
    legend: {
      position: 'top',
    }
  }
};

const barChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      max: 100,
    }
  },
  plugins: {
    legend: {
      position: 'top',
    }
  }
};

const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
    }
  }
};

const Reports = () => {
  const [activeTimeRange, setActiveTimeRange] = useState(timeRanges[1]); // Default to 'Last 30 Days'
  const [isLoading, setIsLoading] = useState(true);
  const [reportType, setReportType] = useState('overview');
  const [exportFormat, setExportFormat] = useState('pdf');
  
  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1200);
    
    return () => clearTimeout(timer);
  }, []);
  
  const handleExportReport = () => {
    // In a real app, this would generate and download the report
    alert(`Exporting ${reportType} report in ${exportFormat.toUpperCase()} format...`);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Analytics & Reports</h1>
        
        <div className="mt-4 md:mt-0 flex flex-col sm:flex-row gap-3">
          <select 
            value={activeTimeRange}
            onChange={(e) => setActiveTimeRange(e.target.value)}
            className="input py-1"
          >
            {timeRanges.map(range => (
              <option key={range} value={range}>{range}</option>
            ))}
          </select>
          
          <div className="flex gap-2">
            <select 
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              className="input py-1"
            >
              <option value="overview">Overview Report</option>
              <option value="campaign">Campaign Performance</option>
              <option value="user">User Progress</option>
              <option value="department">Department Analysis</option>
            </select>
            
            <select 
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="input py-1 w-24"
            >
              <option value="pdf">PDF</option>
              <option value="csv">CSV</option>
              <option value="xlsx">Excel</option>
            </select>
            
            <button 
              onClick={handleExportReport}
              className="btn btn-primary py-1"
            >
              Export
            </button>
          </div>
        </div>
      </div>
      
      {/* Platform Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Phishing Awareness Score</h3>
          <div className="text-3xl font-bold text-primary-600">{mockData.awarenessScore.current}%</div>
          <div className="text-sm text-gray-500">Target: {mockData.awarenessScore.target}%</div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-primary-600 rounded-full" 
              style={{ width: `${(mockData.awarenessScore.current / mockData.awarenessScore.target) * 100}%` }}
            ></div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {mockData.awarenessScore.current > mockData.awarenessScore.initial ? (
              <span className="text-green-600">↑ {mockData.awarenessScore.current - mockData.awarenessScore.initial}% improvement</span>
            ) : (
              <span className="text-red-600">↓ {mockData.awarenessScore.initial - mockData.awarenessScore.current}% decrease</span>
            )}
            {' from initial assessment'}
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Average Click Rate</h3>
          <div className="text-3xl font-bold text-red-600">12%</div>
          <div className="text-sm text-gray-500">Target: &lt;10%</div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-red-600 rounded-full" 
              style={{ width: `${(12 / 30) * 100}%` }}
            ></div>
          </div>
          <div className="mt-2 text-xs text-green-600">
            ↓ 16% reduction from previous period
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Average Report Rate</h3>
          <div className="text-3xl font-bold text-green-600">78%</div>
          <div className="text-sm text-gray-500">Target: &gt;80%</div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-green-600 rounded-full" 
              style={{ width: `${(78 / 80) * 100}%` }}
            ></div>
          </div>
          <div className="mt-2 text-xs text-green-600">
            ↑ 23% increase from previous period
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Course Completion</h3>
          <div className="text-3xl font-bold text-blue-600">65%</div>
          <div className="text-sm text-gray-500">Target: 100%</div>
          <div className="mt-2 h-2 bg-gray-200 rounded-full">
            <div 
              className="h-2 bg-blue-600 rounded-full" 
              style={{ width: '65%' }}
            ></div>
          </div>
          <div className="mt-2 text-xs text-green-600">
            ↑ 12% increase from previous period
          </div>
        </div>
      </div>
      
      {/* Campaign Performance Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Performance Trends</h2>
        <div className="h-80">
          <Line 
            data={mockData.campaignPerformance} 
            options={lineChartOptions}
          />
        </div>
        <div className="mt-4 text-sm text-gray-500">
          The click rate has been steadily decreasing while the report rate has been increasing, indicating improving security awareness among users.
        </div>
      </div>
      
      {/* Training Progress Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Training Completion Progress</h2>
        <div className="h-80">
          <Line 
            data={mockData.trainingProgress} 
            options={{
              ...lineChartOptions,
              plugins: {
                ...lineChartOptions.plugins,
                title: {
                  display: true,
                  text: 'Average Course Completion Rate Over Time'
                }
              }
            }}
          />
        </div>
      </div>
      
      {/* Department Performance */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Phishing Performance by Department</h2>
        <div className="h-80">
          <Bar 
            data={mockData.departmentPerformance} 
            options={barChartOptions}
          />
        </div>
        <div className="mt-4 text-sm text-gray-500">
          Analysis shows that Sales and Marketing departments have the highest click rates and could benefit from additional targeted training.
        </div>
      </div>
      
      {/* Two Column Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Common Phishing Types */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Most Targeted Phishing Types</h2>
          <div className="h-80 flex justify-center">
            <div className="h-full w-full max-w-md">
              <Pie 
                data={mockData.phishingTypes} 
                options={pieChartOptions}
              />
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-500">
            Password reset and account verification simulations remain the most effective for testing user awareness.
          </div>
        </div>
        
        {/* Department Awareness Scores */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Awareness Score by Department</h2>
          <div>
            {mockData.awarenessScore.byDepartment.map(dept => (
              <div key={dept.name} className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">{dept.name}</span>
                  <span className="text-sm font-medium text-gray-900">{dept.score}%</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div 
                    className={`h-2 rounded-full ${
                      dept.score >= 85 ? 'bg-green-600' : 
                      dept.score >= 75 ? 'bg-blue-600' : 
                      dept.score >= 65 ? 'bg-yellow-600' : 
                      'bg-red-600'
                    }`}
                    style={{ width: `${dept.score}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Recent Campaign Results */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Campaign Results</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Targets
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Delivered
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opened
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clicked
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reported
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Click Rate
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Report Rate
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {mockData.recentCampaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {campaign.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.startDate} to {campaign.endDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.targets}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.delivered}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.opened}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                    {campaign.clicked}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                    {campaign.reported}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      {campaign.clickRate}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      {campaign.reportRate}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Action Recommendations */}
      <div className="card bg-blue-50 border border-blue-100">
        <h2 className="text-lg font-semibold text-blue-900 mb-4">Recommendations Based on Data</h2>
        <ul className="space-y-2 text-blue-800">
          <li className="flex items-start">
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-200 text-blue-600 mr-2 mt-0.5">1</span>
            <span>Schedule targeted training for the Sales and Marketing departments focused on identifying social engineering attacks.</span>
          </li>
          <li className="flex items-start">
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-200 text-blue-600 mr-2 mt-0.5">2</span>
            <span>Consider implementing an additional "Password Reset" simulation campaign as this type shows the highest effectiveness in training.</span>
          </li>
          <li className="flex items-start">
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-200 text-blue-600 mr-2 mt-0.5">3</span>
            <span>Create incentives to boost the course completion rate from the current 65% to reach the 100% target.</span>
          </li>
          <li className="flex items-start">
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-200 text-blue-600 mr-2 mt-0.5">4</span>
            <span>Implement follow-up training for users who clicked on phishing simulations in the most recent campaigns.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Reports; 